import React, { useState, useEffect } from 'react';
import { useI18n } from '../../lib/i18n';
import Modal from '../ui/Modal';
import { Button } from '../ui/Button';
import Toggle from '../ui/Toggle';
import {
  STORE_OPERATIONS,
  saveStoreCampaign,
  type StoreCampaignDoc,
  type StoreConnectionDoc,
  type StoreOperation,
} from '../../lib/store';

interface StoreCampaignModalProps {
  open: boolean;
  campaign?: StoreCampaignDoc | null;
  connections: StoreConnectionDoc[];
  onClose: () => void;
  onSaved: () => void;
}

export function StoreCampaignModal({
  open,
  campaign,
  connections,
  onClose,
  onSaved,
}: StoreCampaignModalProps) {
  const { t } = useI18n();

  const [name, setName] = useState('');
  const [connectionId, setConnectionId] = useState('');
  const [operation, setOperation] = useState<StoreOperation>('abandoned_cart');
  const [enabled, setEnabled] = useState(true);
  const [templateId, setTemplateId] = useState('');
  const [templateLanguage, setTemplateLanguage] = useState('en');
  const [delayMin, setDelayMin] = useState(15);
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (campaign) {
      setName(campaign.name);
      setConnectionId(campaign.connectionId);
      setOperation(campaign.operation);
      setEnabled(campaign.enabled);
      setTemplateId(campaign.templateId);
      setTemplateLanguage(campaign.templateLanguage || 'en');
      setDelayMin(campaign.delayMin);
      setCouponCode(campaign.couponCode || '');
      setCouponDiscount(campaign.couponDiscount || '');
    } else {
      setName('');
      setConnectionId(connections[0]?.id || '');
      setOperation('abandoned_cart');
      setEnabled(true);
      setTemplateId('');
      setTemplateLanguage('en');
      setDelayMin(15);
      setCouponCode('');
      setCouponDiscount('');
    }
    setError(null);
  }, [campaign, connections, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t('store.campaigns.nameRequired'));
      return;
    }
    if (!connectionId) {
      setError(t('store.campaigns.connectionRequired'));
      return;
    }
    if (!templateId.trim()) {
      setError(t('store.campaigns.templateRequired'));
      return;
    }

    const selectedConn = connections.find((c) => c.id === connectionId);
    if (!selectedConn) return;

    setSubmitting(true);
    setError(null);
    try {
      await saveStoreCampaign({
        id: campaign?.id,
        name: name.trim(),
        connectionId,
        storeType: selectedConn.storeType,
        operation,
        enabled,
        templateId: templateId.trim(),
        templateLanguage,
        delayMin: Number(delayMin),
        couponCode: couponCode.trim() || null,
        couponDiscount: couponDiscount.trim() || null,
      });
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save campaign');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={campaign ? t('store.campaigns.edit') : t('store.campaigns.create')}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-lg bg-danger-50 p-3 text-xs text-danger dark:bg-rose-950/40 dark:text-rose-400">
            {error}
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
            {t('store.campaigns.name')} *
          </label>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('store.campaignNamePlaceholder') || 'e.g. Abandoned Cart Recovery (15 mins)'}
            className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
              {t('store.campaigns.connection')} *
            </label>
            <select
              value={connectionId}
              onChange={(e) => setConnectionId(e.target.value)}
              className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            >
              {connections.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.storeType})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
              {t('store.campaigns.operation')} *
            </label>
            <select
              value={operation}
              onChange={(e) => setOperation(e.target.value as StoreOperation)}
              className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            >
              {STORE_OPERATIONS.map((op) => (
                <option key={op} value={op}>
                  {t(`store.ops.${op}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-line p-3 dark:border-slate-700">
          <div>
            <span className="block text-xs font-semibold text-ink dark:text-white">
              {t('store.campaigns.status')}
            </span>
            <span className="text-[11px] text-ink-3 dark:text-slate-400">
              {enabled ? t('store.campaigns.enabled') : t('store.campaigns.disabled')}
            </span>
          </div>
          <Toggle
            on={enabled}
            onChange={(v) => setEnabled(v)}
            label={t('store.campaigns.status')}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
              {t('store.campaigns.templateId')} *
            </label>
            <input
              type="text"
              required
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              placeholder="e.g. abandoned_cart_recovery"
              className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            />
            <span className="mt-1 block text-[10px] text-ink-3 dark:text-slate-400">
              Approved WhatsApp template name
            </span>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
              {t('store.campaigns.language')}
            </label>
            <select
              value={templateLanguage}
              onChange={(e) => setTemplateLanguage(e.target.value)}
              className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            >
              <option value="en">English (en)</option>
              <option value="ar">Arabic (ar)</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
            {t('store.campaigns.delayMin')}
          </label>
          <input
            type="number"
            min="0"
            max="10080"
            value={delayMin}
            onChange={(e) => setDelayMin(parseInt(e.target.value, 10) || 0)}
            className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          />
          <span className="mt-1 block text-[10px] text-ink-3 dark:text-slate-400">
            {t('store.campaigns.delayNote')}
          </span>
        </div>

        {operation === 'abandoned_cart' && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
                {t('store.campaigns.couponCode')}
              </label>
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                placeholder="e.g. SAVE10"
                className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
                {t('store.campaigns.couponDiscount')}
              </label>
              <input
                type="text"
                value={couponDiscount}
                onChange={(e) => setCouponDiscount(e.target.value)}
                placeholder="e.g. 10%"
                className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-3 border-t border-line dark:border-slate-700">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" loading={submitting}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
